import React, { useEffect, useMemo, useState } from "react";
import { BiExport, BiFile } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { CSVLink } from "react-csv";
import oDayJs from "dayjs";

import { BUTTON_CONFIG } from "config/constants/Button";
import { Button, ComboBox, DateInput, Table, Text } from "components";

import { actionReportGetUtilities, selectReportsState } from "../redux";
import { UtilitiesMapper } from "../mappers/ReportMapper";
import { CastDate } from "utils/SharedUtils";
import { DATES_FORMATS } from "config/constants/Dates";
import { CURRENCY_VALUES } from "config/constants/Currency";
import { selectLoguedUser } from "redux/auth/AuthSelector";

const csvHeaders = [
  { label: "Código", key: "code" },
  { label: "Artículo", key: "name" },
  { label: "Total Costos", key: "totalCosts" },
  { label: "Total Ventas", key: "totalSales" },
  { label: "Utilidad", key: "utility" },
  { label: "Porcentaje", key: "percentage" },
];

const UtilitiesReport = () => {
  const oDispatch = useDispatch();
  const { aData } = useSelector(selectReportsState);

  const onGenerate = (oData) => oDispatch(actionReportGetUtilities(oData));

  const [oCurrency, setCurrency] = useState(CURRENCY_VALUES[1]);
  const [dDateTo, setDateTo] = useState(oDayJs().toDate());
  const [dDateFrom, setDateFrom] = useState(oDayJs().subtract(1, "month").toDate());

  const oTotals = useMemo(() => {
    return aData.map(UtilitiesMapper).reduce((oAccumulator, oItem) => {
      oAccumulator.costs += oItem.totalCosts;
      oAccumulator.sales += oItem.totalSales;
      oAccumulator.utility += oItem.utility;
      return oAccumulator;
    }, { costs: 0, sales: 0, utility: 0 });
  }, [aData]);

  const oUser = useSelector(selectLoguedUser);
  useEffect(() => {
    if (oUser && oUser.config?.idCurrency)
      setCurrency(CURRENCY_VALUES.find(item => item.value === oUser.config?.idCurrency));
  }, [oUser]);

  const onExport = () => {
    onGenerate({
      hasta: CastDate(dDateTo, DATES_FORMATS.MYSQL_SERVER),
      desde: CastDate(dDateFrom, DATES_FORMATS.MYSQL_SERVER),
      monid: oCurrency.value,
    });
  };

  return (
    <section>
      <section className="grid grid-cols-1 sm:grid-cols-2">
        <h1 className="ui-page-title self-center text-center md:text-left">
          Utilidades en un periodo
        </h1>
        <div className="flex items-center justify-end gap-5">
          {aData?.length > 0 &&
            <CSVLink
              data={aData.map(UtilitiesMapper)}
              className="ml-auto"
              headers={csvHeaders}
              title={`Utilidades en un periodo ${CastDate(new Date(), DATES_FORMATS.LATIN)}`}
              filename={`Utilidades en un periodo ${CastDate(new Date(), DATES_FORMATS.LATIN)}`}
            >
              <Button
                label="Exportar"
                type={BUTTON_CONFIG.CLASS.SUCCESS}
                icon={<BiExport className="text-white ml-2 text-lg" />}
              />
            </CSVLink>
          }
          <Button
            label="Generar reporte"
            onClick={onExport}
            type={BUTTON_CONFIG.CLASS.PRIMARY}
            icon={<BiFile className="text-white ml-2 text-lg" />}
          />
        </div>
      </section>
      <section className="grid grid-cols-1 sm:grid-cols-2 ui-card py-4 px-5 mt-4 gap-5">
        <div className="relative col-span-1 gap-5">
          <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
            <span className="text-gray-500 dark:text-gray-400 text-sm">Fecha inicio</span>
          </div>
          <DateInput
            value={dDateFrom}
            onChange={setDateFrom}
            placeholder="Fecha inicio"
            containerClassName="rounded-lg overflow-hidden ml-20"
            className="ui-field w-full"
          />
        </div>
        <div className="relative col-span-1 gap-5">
          <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
            <span className="text-gray-500 dark:text-gray-400 text-sm">Fecha fin</span>
          </div>
          <DateInput
            value={dDateTo}
            onChange={setDateTo}
            placeholder="Fecha fin"
            containerClassName="rounded-lg overflow-hidden ml-20"
            className="ui-field w-full"
          />
        </div>
        <div className="relative col-span-1 gap-5">
          <ComboBox
            selected={oCurrency}
            onChange={setCurrency}
            label="Moneda"
            emptyLabel="Todas"
            options={[CURRENCY_VALUES[1], CURRENCY_VALUES[2]]}
          />
        </div>
      </section>
      <section className="py-5">
        <Table>
          <Table.Header>
            <Table.Row isHeaderOrFooter>
              <Table.Col scope="col" className="px-3 py-2 text-center">
                Código
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-center">
                Artículo
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-right">
                Total Costos
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-right">
                Total Ventas
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-right">
                Utilidad
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-center">
                Porcentaje
              </Table.Col>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {aData.length > 0 ? (
              aData.map(UtilitiesMapper).map((oItem, nIndex) => (
                <Table.Row key={`${nIndex}`}>
                  <Table.Col
                    scope="row"
                    className="text-xs md:text-sm px-3 py-2 font-medium text-gray-800 dark:text-white whitespace-nowrap text-center"
                  >
                    {oItem.code}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-center">
                    {oItem.name}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-right">
                    {oItem.totalCosts}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-right">
                    {oItem.totalSales}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-right">
                    {oItem.utility}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-center">
                    {oItem.percentage}
                  </Table.Col>
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Col className="px-3 py-2 text-center" colSpan={7}>
                  <em>Sin resultados</em>
                </Table.Col>
              </Table.Row>
            )}
          </Table.Body>
          {aData.length > 0 &&
            <Table.Footer>
              <Table.Row isHeaderOrFooter>
                <Table.Col colSpan="2" className="px-3 py-2 text-right">
                  <Text>Totales:</Text>
                </Table.Col>
                <Table.Col className="px-3 py-2 text-right">
                  <Text>{oTotals.costs}</Text>
                </Table.Col>
                <Table.Col className="px-3 py-2 text-right">
                  <Text>{oTotals.sales}</Text>
                </Table.Col>
                <Table.Col className="px-3 py-2 text-right">
                  <Text>{oTotals.utility}</Text>
                </Table.Col>
                <Table.Col className="px-3 py-2 text-right" />
              </Table.Row>
            </Table.Footer>
          }
        </Table>
      </section>
    </section>
  );
};

export default UtilitiesReport;
